/**
 * Contact Deduplication Service
 * Finds and merges duplicate contact records
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type { Contact } from "../types/contact.types";

export interface DuplicateGroup {
  id: string;
  match_type: "email" | "name" | "phone";
  contacts: Contact[];
  confidence: number; // 0-100
}

export interface MergeResult {
  primary_contact_id: UUID;
  merged_contact_ids: UUID[];
  success: boolean;
  error?: string;
}

/**
 * Find potential duplicate contacts
 */
export async function findDuplicateContacts(): Promise<DuplicateGroup[]> {
  const duplicates: DuplicateGroup[] = [];

  // Find email duplicates
  const { data: emailDupes, error: emailError } = await supabase
    .rpc("find_email_duplicates")
    .catch(() => ({ data: null, error: { message: "RPC not available" } }));

  if (!emailError && emailDupes) {
    // Process email duplicates
    for (const group of emailDupes) {
      duplicates.push({
        id: `email-${group.email}`,
        match_type: "email",
        contacts: group.contacts,
        confidence: 100, // Exact email match
      });
    }
  }

  // Fallback: Query directly for email duplicates
  if (!emailDupes) {
    const { data: contacts } = await supabase
      .from("contacts")
      .select("*")
      .order("email");

    if (contacts) {
      const emailMap = new Map<string, Contact[]>();

      for (const contact of contacts) {
        if (contact.email) {
          const normalizedEmail = contact.email.toLowerCase().trim();
          if (!emailMap.has(normalizedEmail)) {
            emailMap.set(normalizedEmail, []);
          }
          emailMap.get(normalizedEmail)!.push(contact);
        }
      }

      // Add groups with more than one contact
      for (const [email, contactList] of emailMap) {
        if (contactList.length > 1) {
          duplicates.push({
            id: `email-${email}`,
            match_type: "email",
            contacts: contactList,
            confidence: 100,
          });
        }
      }
    }
  }

  // Find phone duplicates
  const { data: phoneDupes } = await supabase
    .rpc("find_phone_duplicates")
    .catch(() => ({
      data: null,
    }));

  if (phoneDupes) {
    for (const group of phoneDupes) {
      duplicates.push({
        id: `phone-${group.phone}`,
        match_type: "phone",
        contacts: group.contacts,
        confidence: 95,
      });
    }
  }

  // Find name duplicates (first + last name match)
  const { data: allContacts } = await supabase
    .from("contacts")
    .select("*")
    .not("first_name", "is", null)
    .not("last_name", "is", null);

  if (allContacts) {
    const nameMap = new Map<string, Contact[]>();

    for (const contact of allContacts) {
      if (contact.first_name && contact.last_name) {
        const normalizedName = `${contact.first_name.toLowerCase().trim()} ${contact.last_name.toLowerCase().trim()}`;
        if (!nameMap.has(normalizedName)) {
          nameMap.set(normalizedName, []);
        }
        nameMap.get(normalizedName)!.push(contact);
      }
    }

    for (const [name, contactList] of nameMap) {
      if (contactList.length > 1) {
        // Check if this group already exists from email matching
        const existingGroup = duplicates.find((d) =>
          d.contacts.some((c) => contactList.some((nc) => nc.id === c.id)),
        );

        if (!existingGroup) {
          duplicates.push({
            id: `name-${name.replace(/\s+/g, "-")}`,
            match_type: "name",
            contacts: contactList,
            confidence: 75, // Name matches are less reliable
          });
        }
      }
    }
  }

  // Sort by confidence (highest first) and then by group size
  return duplicates.sort((a, b) => {
    if (b.confidence !== a.confidence) {
      return b.confidence - a.confidence;
    }
    return b.contacts.length - a.contacts.length;
  });
}

export async function getDuplicateStats(): Promise<{
  total_contacts: number;
  duplicate_groups: number;
  duplicate_contacts: number;
}> {
  const duplicates = await findDuplicateContacts();

  const { count: totalContacts } = await supabase
    .from("contacts")
    .select("*", { count: "exact", head: true })
    .neq("status", "merged");

  const duplicateContacts = duplicates.reduce(
    (sum, group) => sum + group.contacts.length - 1, // -1 because one will be kept
    0,
  );

  return {
    total_contacts: totalContacts || 0,
    duplicate_groups: duplicates.length,
    duplicate_contacts: duplicateContacts,
  };
}

/**
 * Ignore a duplicate group (mark as reviewed)
 */
export async function ignoreDuplicateGroup(
  groupId: string,
  contactIds: UUID[],
): Promise<void> {
  // Store in local storage or a database table that these contacts have been reviewed
  // For now, we'll use local storage
  const ignored = JSON.parse(localStorage.getItem("ignoredDuplicates") || "[]");
  ignored.push({
    groupId,
    contactIds,
    ignoredAt: new Date().toISOString(),
  });
  localStorage.setItem("ignoredDuplicates", JSON.stringify(ignored));
}

/**
 * Check if a duplicate group has been ignored
 */
export function isDuplicateGroupIgnored(groupId: string): boolean {
  const ignored = JSON.parse(localStorage.getItem("ignoredDuplicates") || "[]");
  return ignored.some((i: { groupId: string }) => i.groupId === groupId);
}

/**
 * Clear ignored duplicates
 */
export function clearIgnoredDuplicates(): void {
  localStorage.removeItem("ignoredDuplicates");
}
